import React, { useEffect, useMemo, useRef, useState } from 'react';
import LocomotiveScroll from 'locomotive-scroll';
import 'locomotive-scroll/dist/locomotive-scroll.css';
import Confetti from 'react-confetti-boom';
import './experience.css';

function CircularText() {
  return (
    <div className="circularText" aria-hidden="true">
      <svg viewBox="0 0 200 200" className="circularSvg">
        <defs>
          <path
            id="circlePath"
            d="M 100, 100 m -70, 0 a 70,70 0 1,1 140,0 a 70,70 0 1,1 -140,0"
          />
        </defs>
        <text>
          <textPath href="#circlePath" startOffset="50%" textAnchor="middle">
            • FRONTEND • 3D • WEBXR • GLSL • THREE • R3F •
          </textPath>
        </text>
      </svg>
    </div>
  );
}

function ScatterText({ text, className = '' }) {
  const wrapRef = useRef(null);
  const measureRef = useRef(null);
  const layerRef = useRef(null);

  // Render letters including spaces/newlines
  const chars = useMemo(() => Array.from(text), [text]);

  return (
    <span className={`scatterBlock ${className}`} ref={wrapRef} data-scatter>
      <span className="scatterMeasure" ref={measureRef} aria-hidden="true">
        {chars.map((ch, i) => (
          <span key={`m-${i}`} className={ch === ' ' ? 'space' : ''}>
            {ch === ' ' ? '\u00A0' : ch}
          </span>
        ))}
      </span>

      <span className="scatterLayer" ref={layerRef} aria-label={text}>
        {chars.map((ch, i) => (
          <span
            key={`v-${i}`}
            className={`scatterCh ${ch === ' ' ? 'space' : ''}`}
            data-idx={i}
          >
            {ch === ' ' ? '\u00A0' : ch}
          </span>
        ))}
      </span>
    </span>
  );
}

export default function Experience() {
    const scrollRef = useRef(null);
    const locoRef = useRef(null);
    const stretchRef = useRef(null);
    const principlesRef = useRef(null);
    const scatterRef = useRef(null);
    const helloRef = useRef(null);
    const confettiSectionRef = useRef(null);
    const confettiHasPlayedRef = useRef(false);
    const [confettiKey, setConfettiKey] = useState(0);

  // Asset base path: works in CRA (PUBLIC_URL) and Vite (import.meta.env.BASE_URL)
  const getBase = () => {
    // CRA / Webpack
    if (typeof process !== 'undefined' && process?.env?.PUBLIC_URL) {
      const pu = process.env.PUBLIC_URL;
      return pu.endsWith('/') ? pu : `${pu}/`;
    }

    // Vite
    try {
      // eslint-disable-next-line no-undef
      const viteBase = import.meta?.env?.BASE_URL;
      if (viteBase) return viteBase;
    } catch (_) {
      // ignore
    }

    return '/';
  };

  const BASE = getBase();
  const asset = (p) => `${BASE}${String(p).replace(/^\//, '')}`;

  // Locomotive (best-effort). Note: Locomotive is not always reliable inside a nested
  // overflow container depending on version/layout; we keep a fallback below.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    try {
      const loco = new LocomotiveScroll({
        el,
        smooth: true,
        multiplier: 0.9,
        smartphone: { smooth: true },
        tablet: { smooth: true },
      });
      locoRef.current = loco;

      // Update after images load (important for correct positions).
      const onLoad = () => {
        try {
          loco.update();
        } catch (_) {}
      };

      window.addEventListener('load', onLoad);

      return () => {
        window.removeEventListener('load', onLoad);
        try {
          loco.destroy();
        } catch (_) {}
        locoRef.current = null;
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[Experience] Locomotive init failed, using fallback parallax.', e);
      locoRef.current = null;
      return undefined;
    }
  }, []);

  useEffect(() => {
  const root = scrollRef.current;
  const target = confettiSectionRef.current;
  if (!root || !target) return;

  const io = new IntersectionObserver(
    (entries) => {
      const e = entries[0];
      if (!e) return;

      // déclenche une seule fois quand ~40% est visible
      if (e.isIntersecting && e.intersectionRatio >= -0.5 && !confettiHasPlayedRef.current) {
        confettiHasPlayedRef.current = true;
        setConfettiKey(Date.now()); // remount => boom
      }
    },
    {
    root,
    rootMargin: '0px 0px -35% 0px',
    threshold: [0, 0.05, 0.1, 0.2, 0.4, 0.6, 0.8, 1],
    }
  );

  io.observe(target);
  return () => io.disconnect();
}, []);

  // Fallback parallax for nested scroll containers (always works).
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const targets = () => Array.from(container.querySelectorAll('[data-scroll][data-scroll-speed]'));
    let raf = 0;

    const apply = () => {
      raf = 0;
      const cRect = container.getBoundingClientRect();
      const cCenterY = cRect.top + cRect.height * 0.5;

      for (const el of targets()) {
        const speed = Number(el.getAttribute('data-scroll-speed'));
        // IMPORTANT: don't override Locomotive transforms for horizontal parallax elements.
        // The fallback writes `transform`, which would cancel the horizontal movement and cause jitter.
        const dir = (el.getAttribute('data-scroll-direction') || '').toLowerCase();
        if (dir === 'horizontal' || el.classList.contains('helloWordScroll')) continue;
        if (!Number.isFinite(speed)) continue;

        const r = el.getBoundingClientRect();
        const elCenterY = r.top + r.height * 0.5;

        // -1..1 approx (élément au-dessus/au-dessous du centre)
        const rel = (elCenterY - cCenterY) / cRect.height;

        // Amplitude max en px (évite les gros décalages et le recouvrement)
        const maxShift = 90;
        const y = -rel * speed * maxShift;

        // Clamp fort (sécurité)
        const yClamped = Math.max(-maxShift, Math.min(maxShift, y));

        el.style.transform = `translate3d(0, ${yClamped}px, 0)`;
      }
    };

    const onScroll = () => {
      // If Locomotive is actively transforming things, you can comment out the next line.
      // But in practice this keeps the effect visible even when Locomotive does nothing.
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };

    // Initial run + on scroll
    apply();
    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      for (const el of targets()) {
        el.style.transform = '';
      }
    };
  }, []);

  // Horizontal scroll text (HELLOOO) driven by the nested scroll container
  useEffect(() => {
    const container = scrollRef.current;
    const el = helloRef.current;
    if (!container || !el) return;

    let raf = 0;

    const update = () => {
      raf = 0;
      const max = container.scrollHeight - container.clientHeight;
      if (max <= 0) return;

      const p = container.scrollTop / max; // 0..1

      // Move from right -> left across the viewport
      const startX = window.innerWidth * 0.4;
      const endX = -window.innerWidth * 7;
      const x = startX + (endX - startX) * p;

      el.style.transform = `translate3d(${x}px, 0, 0)`;
    };

    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    // init
    requestAnimationFrame(update);

    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      el.style.transform = '';
    };
  }, []);

  // Auto-place gallery labels into a corner (semi-random controlled via data-pref-corner)
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const wraps = () => Array.from(container.querySelectorAll('.galleryCardWrap'));
    let raf = 0;

    const setCorner = (sideEl, corner) => {
      sideEl.dataset.corner = corner;
      sideEl.classList.remove('corner-tr', 'corner-tl', 'corner-br', 'corner-bl');
      sideEl.classList.add(`corner-${corner}`);
    };

    const apply = () => {
      raf = 0;
      const cRect = container.getBoundingClientRect();
      const pad = 12;

      for (const wrap of wraps()) {
        const side = wrap.querySelector('.gallerySide');
        const card = wrap.querySelector('.galleryCard');
        if (!side || !card) continue;

        // Measure
        const sideRect = side.getBoundingClientRect();
        const r = card.getBoundingClientRect();

        const sideW = sideRect.width;
        const sideH = sideRect.height;

        const rightSpace = cRect.right - r.right;
        const leftSpace = r.left - cRect.left;
        const topSpace = r.top - cRect.top;
        const bottomSpace = cRect.bottom - r.bottom;

        const canRight = rightSpace >= sideW + pad;
        const canLeft = leftSpace >= sideW + pad;
        const canTop = topSpace >= sideH + pad;
        const canBottom = bottomSpace >= sideH + pad;

        // Semi-random controlled: start from a deterministic preferred corner per item
        const pref = (side.getAttribute('data-pref-corner') || 'tr').toLowerCase();
        let corner = ['tr', 'tl', 'br', 'bl'].includes(pref) ? pref : 'tr';

        const fits = (c) => {
          const v = c.startsWith('t') ? canTop : canBottom;
          const h = c.endsWith('r') ? canRight : canLeft;
          return v && h;
        };

        // Try preferred first, then the other corners
        const order = ['tr', 'br', 'tl', 'bl'];
        const candidates = [corner, ...order.filter((c) => c !== corner)];

        const found = candidates.find(fits);
        if (found) {
          corner = found;
        } else {
          // No perfect fit: pick the corner with the most available area
          const score = (c) => {
            const h = c.endsWith('r') ? rightSpace : leftSpace;
            const v = c.startsWith('t') ? topSpace : bottomSpace;
            return h * v;
          };
          corner = candidates.sort((a, b) => score(b) - score(a))[0] || corner;
        }

        setCorner(side, corner);
      }
    };

    // 1) Replace onScroll with requestApply
    const requestApply = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };

    // 2) Initial + listeners: only on resize, not on scroll
    // Choose the corner once (after assets/layout are ready)
    apply();

    // Re-evaluate only on resize (not on scroll) so labels stay pinned
    window.addEventListener('resize', requestApply);

    // 3) Update after images load
    const imgs = Array.from(container.querySelectorAll('.galleryImg'));
    const onImgLoad = () => requestApply();
    imgs.forEach((img) => img.addEventListener('load', onImgLoad));

    return () => {
      if (raf) cancelAnimationFrame(raf);
      // 4) Remove only resize listener
      window.removeEventListener('resize', requestApply);
      // 5) Keep removing load listeners on images
      imgs.forEach((img) => img.removeEventListener('load', onImgLoad));
    };
  }, []);

  // Scroll-driven stretch + weight (peaks when the word is around the center of the scroll container)
  useEffect(() => {
    const container = scrollRef.current;
    const target = stretchRef.current;
    if (!container || !target) return;

    let raf = 0;

    const clamp01 = (v) => Math.max(0, Math.min(1, v));
    const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

    const update = () => {
      raf = 0;

      const cRect = container.getBoundingClientRect();
      const tRect = target.getBoundingClientRect();

      const containerCenterY = cRect.top + cRect.height * 0.5;
      const wordCenterY = tRect.top + tRect.height * 0.5;

      // Range around center where the effect is active (in px)
      const range = Math.min(260, cRect.height * 0.35);
      const dist = Math.abs(wordCenterY - containerCenterY);

      // 1 at center, 0 outside the range
      const raw = 1 - dist / range;
      const t = easeInOut(clamp01(raw));

      // Write CSS vars (used by transform + variable font weight)
      const sx = 1 + 0.35 * t;
      target.style.setProperty('--t', String(t));
      target.style.setProperty('--sx', String(sx));
    };

    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    // Initial + listeners
    update();
    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      target.style.removeProperty('--t');
      target.style.removeProperty('--sx');
    };
  }, []);

  // Scatter letters -> assemble on scroll (for the Projects intro sentence)
  useEffect(() => {
    const container = scrollRef.current;
    const wrap = scatterRef.current;
    if (!container || !wrap) return;

    const measure = wrap.querySelector('.scatterMeasure');
    const layer = wrap.querySelector('.scatterLayer');
    if (!measure || !layer) return;

    const visChars = Array.from(layer.querySelectorAll('.scatterCh'));
    const measChars = Array.from(measure.querySelectorAll('span'));

    // Deterministic pseudo-random for stable scatter
    const rand = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };

    let raf = 0;
    let prepared = false;

    const prep = () => {
      // Ensure the measure layer matches the font/styles of the visible one
      const wrapRect = wrap.getBoundingClientRect();
      const mRect = measure.getBoundingClientRect();

      // Set exact height so layout is stable
      const h = Math.max(1, mRect.height);
      layer.style.minHeight = `${h}px`;

      // Compute target positions relative to the wrapper
      for (let i = 0; i < visChars.length; i++) {
        const v = visChars[i];
        const m = measChars[i];
        if (!v || !m) continue;

        const r = m.getBoundingClientRect();
        const tx = r.left - wrapRect.left;
        const ty = r.top - (wrapRect.top - 100);

        // Place each char at its final (target) position
        v.style.left = `${tx}px`;
        v.style.top = `${ty}px`;

        // Initial scatter offset (bigger for non-space chars)
        const isSpace = v.classList.contains('space');
        const s = i * 100;
        const rx = (rand(s + 1.1) - 0.5);
        const ry = (rand(s + 2.2) - 0.5);

        // Use the scroll container size so letters can scatter in a large area
        const cRect = container.getBoundingClientRect();

        // Bigger scatter so the effect is noticeable as soon as the section approaches
        const spreadX = isSpace ? 0 : Math.min(760, Math.max(180, cRect.width * 0.65));
        const spreadY = isSpace ? 0 : Math.min(420, Math.max(140, cRect.height * 0.50));

        v.dataset.dx0 = String(rx * spreadX);
        v.dataset.dy0 = String(ry * spreadY);
      }

      prepared = true;
    };

    const clamp01 = (v) => Math.max(0, Math.min(1, v));
    // Slight easing so you can perceive the movement more clearly
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

    const update = () => {
      raf = 0;
      if (!prepared) prep();

      const cRect = container.getBoundingClientRect();
      const wRect = wrap.getBoundingClientRect();

      // Start a bit later so when you reach the section for the first time
      // the sentence is still scattered.
      const focusY = cRect.top + cRect.height * 0.10;
      const blockCenterY = wRect.top + wRect.height * 0.5;

      // Slightly tighter active range => avoids assembling too early
      const range = Math.min(640, cRect.height * 0.18);
      const dist = Math.abs(blockCenterY - focusY);

      // Keep a readable pause once assembled (but not too long)
      const hold = Math.min(200, range * 0.34);

      // 1 inside hold, then fades out linearly until `range`
      const raw = dist <= hold ? 1 : 1 - (dist - hold) / (range - hold);
      const t = ease(clamp01(raw));

      // Move each letter from scattered offset -> 0
      for (const v of visChars) {
        const dx0 = Number(v.dataset.dx0 || 0);
        const dy0 = Number(v.dataset.dy0 || 0);

        // When you leave the assembled state, push letters much farther away
        // (so the end of the animation feels more dramatic)
        const out = 1 - t; // 0 (assembled) -> 1 (fully scattered)
        const fly = 1 + out * 2.8; // up to ~3.8x farther at the end

        const dx = dx0 * out * fly;
        const dy = dy0 * out * fly;

        v.style.setProperty('--dx', `${dx}px`);
        v.style.setProperty('--dy', `${dy}px`);
        v.style.setProperty('--a', String(0.30 + 0.70 * t));
      }
    };

    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    // Prepare once after paint + update as you scroll
    requestAnimationFrame(() => {
      prep();
      update();
    });

    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    // If fonts/images shift layout, re-prep once everything is loaded
    const onLoad = () => {
      prepared = false;
      onScroll();
    };
    window.addEventListener('load', onLoad);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('load', onLoad);

      // cleanup vars
      for (const v of visChars) {
        v.style.removeProperty('--dx');
        v.style.removeProperty('--dy');
        v.style.removeProperty('--a');
        v.style.left = '';
        v.style.top = '';
      }
      layer.style.minHeight = '';
    };
  }, []);

  // Principles: scroll-driven “deal cards” motion (left / up / right)
  useEffect(() => {
    const container = scrollRef.current;
    const section = principlesRef.current;
    if (!container || !section) return;

    let raf = 0;

    const clamp01 = (v) => Math.max(0, Math.min(1, v));

    const update = () => {
      raf = 0;

      const top = container.scrollTop;
      const vh = container.clientHeight;
      const start = section.offsetTop;
      const end = start + section.offsetHeight - vh;

      if (end <= start) {
        section.style.setProperty('--p1', '0');
        section.style.setProperty('--p2', '0');
        section.style.setProperty('--p3', '0');
        return;
      }

      const t = clamp01((top - start) / (end - start));
      const seg = 1 / 3;

      // Each progress activates in its third of the section
      const p1 = clamp01(t / seg);
      const p2 = clamp01((t - seg) / seg);
      const p3 = clamp01((t - 2 * seg) / seg);

      section.style.setProperty('--p1', String(p1));
      section.style.setProperty('--p2', String(p2));
      section.style.setProperty('--p3', String(p3));
    };

    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    update();
    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      section.style.removeProperty('--p1');
      section.style.removeProperty('--p2');
      section.style.removeProperty('--p3');
    };
  }, []);

  return (
    <div className="container exp_container">
      <div className="containerExperience" ref={scrollRef} data-scroll-container>
        <section className="hero" data-scroll-section>
          <CircularText />
          <div className="heroIntro">
            {/* <p className="kicker">Expérience</p> */}

            <h1 className="bigTitle">
              FRONTEND DEVELOPER
              <br />
              &amp; FREELANCER
            </h1>

            <br /><br />

            <svg class="arrow-annotation arrow_down" width="132" height="45" viewBox="0 0 72 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M61.907 11.0377C61.91 10.9039 61.8635 10.8138 61.7071 10.8178L61.3446 10.457C60.8734 10.1244 60.373 9.90843 59.7753 10.0946C58.3261 9.79534 56.8275 9.79044 55.3831 9.46036C54.3982 9.2415 53.4757 8.67194 52.3887 8.91571L52.1764 8.6589C52.1471 8.4838 51.989 8.46462 51.8643 8.39134C50.4617 7.5579 48.8746 7.35333 47.3072 7.07338L47.2384 7.14011L47.3123 7.08134C46.1083 5.99125 44.4246 5.98395 43.0532 5.26681C42.9941 5.20252 42.9215 5.15154 42.8404 5.1174C42.7593 5.08326 42.6717 5.06676 42.5834 5.06901C41.7055 4.69437 40.7841 4.42687 39.8398 4.2725C38.637 3.54951 37.2764 3.86236 35.9872 3.71486C34.0949 3.61563 32.1948 3.43947 30.333 3.98272C29.9706 3.91357 29.6175 3.82373 29.2407 3.96784C27.347 4.69993 25.322 4.85375 23.347 5.22373C21.4725 5.64111 19.5499 5.85413 17.718 6.47921L13.568 7.36156C12.4882 7.48388 11.5135 8.07615 10.3973 8.02406C9.66284 8.18895 8.89532 8.08447 8.20989 8.44937L2.5026 9.03806C2.47179 9.06011 2.43494 9.07256 2.39684 9.07381L2.5026 9.03806C2.07554 8.90713 1.61794 8.90719 1.19093 9.03824C0.192171 9.272 0.0292266 9.54566 0.304016 10.5181C1.12899 11.0478 1.97243 10.6018 2.78765 10.5095C4.737 10.291 6.75191 10.4927 8.61547 9.69011C10.1022 9.51467 11.5978 9.39132 13.0102 8.84391C13.2654 8.90178 13.5282 8.92021 13.7892 8.89853C15.8622 8.41589 17.9301 7.90479 20.0004 7.40334C20.0494 7.36124 20.0835 7.30501 20.0978 7.24271L20.3657 7.20881L21.4852 7.09532L21.5197 7.08701C22.4617 6.79736 23.5071 7.09575 24.4196 6.59218L25.9109 6.25598C26.1868 6.24732 26.4481 6.13169 26.6374 5.93439C27.1858 5.94232 27.7417 6.08863 28.2782 5.86608L29.8071 5.27623C30.8885 5.38893 31.9253 5.0201 33.0092 5.00119C34.7431 4.97182 36.4368 5.34819 38.1602 5.31419C39.7435 5.49513 41.132 6.40976 42.7367 6.51799C43.2882 6.97546 43.9674 7.16058 44.6242 7.3931L45.0978 7.54391C45.7103 8.00813 46.5289 7.89154 47.1792 8.25251L52.6082 10.0878C52.6564 10.1317 52.7166 10.1609 52.7815 10.1718C53.4884 10.7504 54.3372 10.8548 55.2133 10.8808C55.3435 11.0115 55.4935 11.1217 55.658 11.2075C57.4418 11.7771 59.3415 11.6729 61.1606 12.0999C61.7492 12.2352 62.1736 11.744 61.907 11.0377ZM32.8234 4.34702L32.8204 4.36002L32.805 4.35659L32.8234 4.34702ZM19.4721 6.87581L19.4781 6.84981L19.4936 6.85324C19.4992 6.84897 19.5043 6.84396 19.5086 6.83835L19.5201 6.85913L19.4936 6.85324L19.4721 6.87581ZM39.7748 4.33322L39.7615 4.33027L39.845 4.25999L39.7748 4.33322ZM1.76578 9.29358L1.73928 9.28769L1.76707 9.27791C1.76857 9.28032 1.76927 9.28314 1.76904 9.28596C1.76881 9.28877 1.76766 9.29144 1.76578 9.29358ZM24.3785 6.54888L24.4024 6.58609L24.3785 6.54888Z" fill="#F43326"></path>
                <path d="M71.2145 7.92775L69.0949 6.94143C68.0977 6.37331 67.1188 5.76596 65.9474 5.58065C65.765 5.50591 65.5495 5.47394 65.5006 5.23293C64.9765 4.79739 64.5257 4.22546 63.7156 4.34607C62.0066 3.49206 60.0948 3.2765 58.2951 2.68487L54.8744 1.04917C54.5477 0.311184 54.0587 0.639915 53.6777 0.933416C53.1339 1.3675 52.7147 1.93313 52.4614 2.57463C52.4481 2.57168 52.4326 2.58874 52.4156 2.61229C51.9823 2.95114 51.0914 3.1905 52.1641 3.86427C52.9585 4.44651 53.2687 3.74533 53.6849 3.30927L54.5502 2.81814C57.1965 3.68464 59.7633 4.77499 62.4676 5.49034C63.2622 5.69895 64.1974 5.59934 64.8426 6.26462C65.6005 7.00508 66.6791 7.16977 67.5214 7.73533C68.0102 8.16302 68.5587 8.42173 69.245 8.3146L69.4498 8.62445C69.0712 10.8712 67.5259 12.4393 65.9327 13.8121C63.7347 15.7043 61.8137 17.8246 59.8589 19.9304C59.4186 20.1082 59.1902 20.4949 58.9281 20.8467L58.9953 20.9073L58.9406 20.8427L58.399 21.178L57.7918 21.0429C57.8562 20.7633 57.9047 20.4824 57.962 20.2035C58.3887 19.8632 58.3827 19.3857 58.3873 18.9127C58.4012 18.9093 58.4143 18.9032 58.4259 18.8948C58.4374 18.8864 58.4471 18.8758 58.4545 18.8636C58.4618 18.8515 58.4666 18.838 58.4686 18.824C58.4706 18.8101 58.4697 18.7958 58.4661 18.7822C58.7008 18.9118 58.7194 18.6904 58.8075 18.5801C58.9761 18.4816 59.1009 18.3246 59.1572 18.14C59.2135 17.9555 59.1971 17.7569 59.1114 17.5836C58.9627 17.3227 58.6906 17.6267 58.5154 17.7928C58.0718 17.9448 58.3633 18.3924 58.1698 18.6274C57.0384 20.0414 56.7551 21.8445 55.9112 23.3908C55.764 23.6566 55.6868 23.9516 56.0881 24.1138C56.1075 24.1318 56.1287 24.148 56.1513 24.162C56.7804 24.605 57.2356 24.312 57.635 23.7674C58.0552 23.5259 58.5516 23.3765 58.7482 22.8757C60.7251 21.3489 62.3583 19.47 64.0754 17.6805C64.9908 17.3349 65.0686 16.2015 65.9194 15.7937C65.9471 15.785 65.9717 15.7685 65.99 15.7463C66.0083 15.7241 66.0196 15.697 66.0225 15.6686L66.4972 15.4825C66.9388 15.5807 66.9772 15.1723 67.1835 14.9721C67.3254 14.6185 67.6681 14.451 67.9284 14.2081C68.8451 13.0608 69.9864 12.0773 70.5809 10.6966C70.9682 10.3143 71.327 9.90523 71.6547 9.47252C72.037 8.8512 72.1535 8.23458 71.2145 7.92775ZM61.6864 3.94491L61.7018 3.94835L61.6864 3.94491ZM54.1616 1.3418C54.1616 1.3418 54.1616 1.3418 54.1646 1.3288L54.1748 1.34475L54.1616 1.3418Z" fill="#F43326"></path>
            </svg>
            
          </div>
        </section>

        <section className="helloo">
            <div>
            <div className="helloRow" aria-label="Hello">
                <span ref={helloRef} className="helloWord helloWordScroll">
                    HELLOOO
                </span>

                {/* <span className="helloWord">OOO</span> */}
            </div>

            {/* <img src="assets/images/experience/paul_marechal.png" className="paul_icone" alt="" /> */}

            <br/><br/><br/><br/><br/><br/>
            <div className="heroMeta">
              <p className="heroCopy">
                Je m’appelle Paul. Je conçois des interfaces et des expériences web (2D/3D) 
                avec une attention particulière sur l’animation, l’interaction et la performance.
              </p>

              <div className="heroLinks">
                <a className="heroLink" href="#projects">More about me</a>
                <a className="heroLink" href="#projects">View work</a>
              </div>
            </div>
          </div>

          {/* <span className="helloPhoto" data-scroll data-scroll-speed="1.2">
                <img
                  id="paul_img"
                  src={asset('assets/images/experience/paul_img.webp')}
                  alt="Paul Maréchal"
                  loading="lazy"
                />
            </span> */}
        

            

            <div className="facts">
                <div className="fact">
                    <svg class="arrow-annotation" width="32" height="32" viewBox="0 0 61 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M50.23 13.3582C50.6404 13.4708 51.2188 13.5674 51.1364 14.2728C51.019 15.287 51.5533 15.5473 52.4373 15.3813C53.347 15.0802 53.9468 14.2703 53.8845 13.4985C53.8331 12.8681 52.9262 12.4458 52.1286 12.3844C51.9378 12.3704 51.7676 12.1695 51.5544 12.3368C50.6623 12.0812 49.8415 11.4948 48.8443 11.7365C48.1063 11.4746 47.3905 10.9258 46.5834 11.5634L46.2148 11.4315C44.8117 10.9136 43.3109 10.7219 41.8243 10.8708L40.6971 10.8328L39.114 10.7044L39.1268 10.7693C39.1268 10.7693 39.1268 10.7693 39.1195 10.7845L39.1159 10.7053L37.0597 10.6329L33.7028 10.3667L31.6341 10.3538L30.1538 10.4988L29.4935 10.4369C29.3545 10.3416 29.1909 10.2894 29.0228 10.2868C28.8548 10.2843 28.6897 10.3314 28.5479 10.4225C28.2027 10.4686 27.8348 10.4027 27.5222 10.622L27.0555 10.6647C26.3684 10.2683 25.7531 10.754 25.1045 10.8269C23.6698 10.8582 22.2244 10.767 20.8126 11.1215C20.5629 11.095 20.31 11.0316 20.0723 11.1728C19.769 11.0675 19.4391 11.071 19.1381 11.1827L17.4688 11.3004L15.5794 11.3639C15.1621 11.1354 14.7672 11.4339 14.3644 11.4212C13.6587 11.231 12.9111 11.2746 12.2318 11.5457C11.5632 11.5783 10.9555 11.099 10.2588 11.3007L6.59117 10.7153C5.30325 10.4644 4.12349 9.81813 3.21306 8.86491C2.66335 8.29448 2.13011 7.95967 1.34854 8.09153C0.387882 8.43091 0.241869 9.03231 1.00891 9.83622C2.25311 11.1357 3.91696 11.9427 5.69945 12.1112C6.49924 12.2932 7.29735 12.4932 8.13131 12.3683L8.78112 12.4133C9.82696 12.7162 10.8784 12.9305 11.9667 12.6445L11.9573 12.6398L13.7347 12.7605C13.9402 12.9366 14.182 12.835 14.4046 12.8553C14.7058 13.069 15.018 12.9902 15.3318 12.8935C16.0953 12.6402 16.9071 12.8117 17.6514 12.6896C20.1035 12.2882 22.5751 12.2318 25.0369 12.0415C25.2402 12.1836 25.4532 12.0936 25.6631 12.0678C26.3435 11.7267 27.2691 12.4186 27.8243 11.5469L28.2777 11.4789C28.5304 11.6728 28.8314 11.7921 29.1473 11.8235C29.4632 11.855 29.7815 11.7973 30.0669 11.657C30.7837 11.6833 31.5128 11.8 32.2125 11.703C32.6426 11.6447 33.2304 11.8421 33.5235 11.2778C34.0048 11.3736 34.3693 11.8413 34.9297 11.7345C35.9164 12.0975 36.9323 11.6773 37.9202 11.797C37.9787 11.8143 38.039 11.825 38.0999 11.8291C38.4895 12.2739 38.9988 11.8393 39.4266 12.0121C40.051 12.2158 40.6714 12.1243 41.295 11.9922C42.8906 12.4176 44.5898 12.0355 46.1889 12.4438C46.3054 12.5011 46.431 12.5441 46.553 12.5946C46.5941 12.5914 46.6333 12.5919 46.6726 12.5924C46.7759 12.6432 46.8874 12.6769 46.9935 12.7267C47.2069 12.7659 47.4025 12.9723 47.6484 12.8141C48.505 12.9794 49.3792 13.1229 50.23 13.3582ZM2.30952 9.03768L2.32456 9.04507L2.30952 9.03768ZM41.8762 11.0136L41.8698 11.0269L41.8781 11.0098L41.8762 11.0136Z" fill="#F43326"></path>
                        <path d="M61.0511 11.9265C60.5026 11.4739 60.0134 10.884 59.1912 10.9654C57.3609 10.0775 55.5244 9.20763 53.707 8.29322C52.8411 7.86763 52.0592 7.24874 51.1771 6.88086C49.0688 6.02991 46.8744 5.42038 44.8022 4.45115C44.2107 4.17449 43.5607 3.84093 42.8461 3.85324C42.4549 3.70084 42.1122 3.35646 41.6277 3.48893C39.444 2.47656 37.033 2.07022 34.8901 0.944233C34.7818 0.8906 34.6604 0.869693 34.5405 0.884017C34.4207 0.898341 34.3075 0.947289 34.2145 1.02498C33.4599 1.1443 33.5185 1.65863 33.6625 2.20319C33.9659 3.34684 35.9837 4.56848 37.4545 4.50568L37.4275 4.41732L37.4508 4.50385C37.5053 4.53062 37.5717 4.53745 37.6326 4.55095C38.1972 4.7581 38.6094 4.42362 39.041 4.15023C41.567 5.01183 44.1203 5.80712 46.6127 6.75773C48.2814 7.39266 49.9773 8.02918 51.5407 8.86329C53.0345 9.65852 54.568 10.4005 56.0243 11.2735C56.5179 11.57 57.4115 11.4323 57.485 12.0313C57.5503 12.556 56.718 12.8412 56.3924 13.3332L56.4527 13.3816L56.3943 13.3341C55.6906 13.6755 55.2336 14.2953 54.7116 14.8526C54.3986 14.8559 54.3953 15.1662 54.2644 15.3364C52.7881 17.1127 51.3117 18.8889 49.8354 20.6652L49.876 20.7063L49.8409 20.6633C48.3878 21.9803 47.21 23.5358 46.0388 25.0969C45.2799 25.7888 44.6888 26.6241 44.076 27.4417C44.0174 27.4736 43.971 27.5242 43.9439 27.5855C43.2177 27.9158 42.8343 28.5999 42.3328 29.1626C42.0274 29.3761 41.7407 29.6159 41.4761 29.8791C41.0865 30.3303 41.2142 30.8411 41.4482 31.329C41.644 31.7372 41.9513 31.7311 42.2827 31.5258C43.1795 31.2465 43.0425 30.2058 43.6433 29.6684C45.9291 27.6606 47.5527 25.0412 49.6876 22.8889C49.7949 22.815 49.8956 22.7319 49.9886 22.6404C51.6533 20.644 53.3172 18.6394 54.9805 16.6266C55.0829 16.5925 55.1862 16.5565 55.2877 16.5196L55.8504 16.822C55.8478 16.8253 55.8459 16.8292 55.845 16.8334L55.9574 16.7737C55.9424 16.7928 55.9236 16.8087 55.9024 16.8204C55.8812 16.8322 55.8579 16.8395 55.8339 16.842C55.8718 16.985 55.8213 17.2534 56.0078 17.2184C56.2866 17.1654 56.2426 16.799 56.3957 16.6021C56.631 16.6826 56.6728 16.4803 56.7536 16.3418C56.9175 16.3286 57.0734 16.2651 57.2006 16.16C57.3277 16.0548 57.42 15.9129 57.465 15.7532C57.7144 15.5639 58.0871 15.5618 58.2425 15.23C58.756 15.1476 59.22 14.8733 59.5425 14.4617C60.2868 14.0746 60.4589 13.1975 61.0467 12.6678C61.0989 12.6217 61.1408 12.5648 61.1696 12.5011C61.1984 12.4374 61.2135 12.3682 61.214 12.2982C61.2144 12.2281 61.2001 12.1587 61.172 12.0947C61.1439 12.0306 61.1027 11.9733 61.0511 11.9265ZM58.6662 11.7839C58.6691 11.7638 58.6733 11.744 58.6787 11.7244C58.6894 11.7648 58.6992 11.8072 58.7071 11.8486C58.6896 11.8297 58.6758 11.8078 58.6662 11.7839ZM54.3397 15.3922C54.3342 15.4036 54.3278 15.4169 54.3223 15.4283L54.2897 15.3465L54.3397 15.3922ZM46.1042 25.183L46.0996 25.1924L46.068 25.1183L46.1042 25.183Z" fill="#F43326"></path>
                    </svg>
                    <span className="factLabel">ㅤParcours</span>
                    <p>Restauration haut de gamme <br/>Reconversion développeur web <br/>Responsable IT chez AeroBay <br/>Création de DevXR</p>
                </div>
                <div className="fact">
                    <svg class="arrow-annotation" width="32" height="32" viewBox="0 0 61 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M50.23 13.3582C50.6404 13.4708 51.2188 13.5674 51.1364 14.2728C51.019 15.287 51.5533 15.5473 52.4373 15.3813C53.347 15.0802 53.9468 14.2703 53.8845 13.4985C53.8331 12.8681 52.9262 12.4458 52.1286 12.3844C51.9378 12.3704 51.7676 12.1695 51.5544 12.3368C50.6623 12.0812 49.8415 11.4948 48.8443 11.7365C48.1063 11.4746 47.3905 10.9258 46.5834 11.5634L46.2148 11.4315C44.8117 10.9136 43.3109 10.7219 41.8243 10.8708L40.6971 10.8328L39.114 10.7044L39.1268 10.7693C39.1268 10.7693 39.1268 10.7693 39.1195 10.7845L39.1159 10.7053L37.0597 10.6329L33.7028 10.3667L31.6341 10.3538L30.1538 10.4988L29.4935 10.4369C29.3545 10.3416 29.1909 10.2894 29.0228 10.2868C28.8548 10.2843 28.6897 10.3314 28.5479 10.4225C28.2027 10.4686 27.8348 10.4027 27.5222 10.622L27.0555 10.6647C26.3684 10.2683 25.7531 10.754 25.1045 10.8269C23.6698 10.8582 22.2244 10.767 20.8126 11.1215C20.5629 11.095 20.31 11.0316 20.0723 11.1728C19.769 11.0675 19.4391 11.071 19.1381 11.1827L17.4688 11.3004L15.5794 11.3639C15.1621 11.1354 14.7672 11.4339 14.3644 11.4212C13.6587 11.231 12.9111 11.2746 12.2318 11.5457C11.5632 11.5783 10.9555 11.099 10.2588 11.3007L6.59117 10.7153C5.30325 10.4644 4.12349 9.81813 3.21306 8.86491C2.66335 8.29448 2.13011 7.95967 1.34854 8.09153C0.387882 8.43091 0.241869 9.03231 1.00891 9.83622C2.25311 11.1357 3.91696 11.9427 5.69945 12.1112C6.49924 12.2932 7.29735 12.4932 8.13131 12.3683L8.78112 12.4133C9.82696 12.7162 10.8784 12.9305 11.9667 12.6445L11.9573 12.6398L13.7347 12.7605C13.9402 12.9366 14.182 12.835 14.4046 12.8553C14.7058 13.069 15.018 12.9902 15.3318 12.8935C16.0953 12.6402 16.9071 12.8117 17.6514 12.6896C20.1035 12.2882 22.5751 12.2318 25.0369 12.0415C25.2402 12.1836 25.4532 12.0936 25.6631 12.0678C26.3435 11.7267 27.2691 12.4186 27.8243 11.5469L28.2777 11.4789C28.5304 11.6728 28.8314 11.7921 29.1473 11.8235C29.4632 11.855 29.7815 11.7973 30.0669 11.657C30.7837 11.6833 31.5128 11.8 32.2125 11.703C32.6426 11.6447 33.2304 11.8421 33.5235 11.2778C34.0048 11.3736 34.3693 11.8413 34.9297 11.7345C35.9164 12.0975 36.9323 11.6773 37.9202 11.797C37.9787 11.8143 38.039 11.825 38.0999 11.8291C38.4895 12.2739 38.9988 11.8393 39.4266 12.0121C40.051 12.2158 40.6714 12.1243 41.295 11.9922C42.8906 12.4176 44.5898 12.0355 46.1889 12.4438C46.3054 12.5011 46.431 12.5441 46.553 12.5946C46.5941 12.5914 46.6333 12.5919 46.6726 12.5924C46.7759 12.6432 46.8874 12.6769 46.9935 12.7267C47.2069 12.7659 47.4025 12.9723 47.6484 12.8141C48.505 12.9794 49.3792 13.1229 50.23 13.3582ZM2.30952 9.03768L2.32456 9.04507L2.30952 9.03768ZM41.8762 11.0136L41.8698 11.0269L41.8781 11.0098L41.8762 11.0136Z" fill="#F43326"></path>
                        <path d="M61.0511 11.9265C60.5026 11.4739 60.0134 10.884 59.1912 10.9654C57.3609 10.0775 55.5244 9.20763 53.707 8.29322C52.8411 7.86763 52.0592 7.24874 51.1771 6.88086C49.0688 6.02991 46.8744 5.42038 44.8022 4.45115C44.2107 4.17449 43.5607 3.84093 42.8461 3.85324C42.4549 3.70084 42.1122 3.35646 41.6277 3.48893C39.444 2.47656 37.033 2.07022 34.8901 0.944233C34.7818 0.8906 34.6604 0.869693 34.5405 0.884017C34.4207 0.898341 34.3075 0.947289 34.2145 1.02498C33.4599 1.1443 33.5185 1.65863 33.6625 2.20319C33.9659 3.34684 35.9837 4.56848 37.4545 4.50568L37.4275 4.41732L37.4508 4.50385C37.5053 4.53062 37.5717 4.53745 37.6326 4.55095C38.1972 4.7581 38.6094 4.42362 39.041 4.15023C41.567 5.01183 44.1203 5.80712 46.6127 6.75773C48.2814 7.39266 49.9773 8.02918 51.5407 8.86329C53.0345 9.65852 54.568 10.4005 56.0243 11.2735C56.5179 11.57 57.4115 11.4323 57.485 12.0313C57.5503 12.556 56.718 12.8412 56.3924 13.3332L56.4527 13.3816L56.3943 13.3341C55.6906 13.6755 55.2336 14.2953 54.7116 14.8526C54.3986 14.8559 54.3953 15.1662 54.2644 15.3364C52.7881 17.1127 51.3117 18.8889 49.8354 20.6652L49.876 20.7063L49.8409 20.6633C48.3878 21.9803 47.21 23.5358 46.0388 25.0969C45.2799 25.7888 44.6888 26.6241 44.076 27.4417C44.0174 27.4736 43.971 27.5242 43.9439 27.5855C43.2177 27.9158 42.8343 28.5999 42.3328 29.1626C42.0274 29.3761 41.7407 29.6159 41.4761 29.8791C41.0865 30.3303 41.2142 30.8411 41.4482 31.329C41.644 31.7372 41.9513 31.7311 42.2827 31.5258C43.1795 31.2465 43.0425 30.2058 43.6433 29.6684C45.9291 27.6606 47.5527 25.0412 49.6876 22.8889C49.7949 22.815 49.8956 22.7319 49.9886 22.6404C51.6533 20.644 53.3172 18.6394 54.9805 16.6266C55.0829 16.5925 55.1862 16.5565 55.2877 16.5196L55.8504 16.822C55.8478 16.8253 55.8459 16.8292 55.845 16.8334L55.9574 16.7737C55.9424 16.7928 55.9236 16.8087 55.9024 16.8204C55.8812 16.8322 55.8579 16.8395 55.8339 16.842C55.8718 16.985 55.8213 17.2534 56.0078 17.2184C56.2866 17.1654 56.2426 16.799 56.3957 16.6021C56.631 16.6826 56.6728 16.4803 56.7536 16.3418C56.9175 16.3286 57.0734 16.2651 57.2006 16.16C57.3277 16.0548 57.42 15.9129 57.465 15.7532C57.7144 15.5639 58.0871 15.5618 58.2425 15.23C58.756 15.1476 59.22 14.8733 59.5425 14.4617C60.2868 14.0746 60.4589 13.1975 61.0467 12.6678C61.0989 12.6217 61.1408 12.5648 61.1696 12.5011C61.1984 12.4374 61.2135 12.3682 61.214 12.2982C61.2144 12.2281 61.2001 12.1587 61.172 12.0947C61.1439 12.0306 61.1027 11.9733 61.0511 11.9265ZM58.6662 11.7839C58.6691 11.7638 58.6733 11.744 58.6787 11.7244C58.6894 11.7648 58.6992 11.8072 58.7071 11.8486C58.6896 11.8297 58.6758 11.8078 58.6662 11.7839ZM54.3397 15.3922C54.3342 15.4036 54.3278 15.4169 54.3223 15.4283L54.2897 15.3465L54.3397 15.3922ZM46.1042 25.183L46.0996 25.1924L46.068 25.1183L46.1042 25.183Z" fill="#F43326"></path>
                    </svg>
                    <span className="factLabel">ㅤPassions</span>
                    <p>Cuisine<br/>Randonnée<br/>Vélo tourisme<br/>Cinématographie </p>
                </div>
                <div className="fact">
                    <svg class="arrow-annotation" width="32" height="32" viewBox="0 0 61 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M50.23 13.3582C50.6404 13.4708 51.2188 13.5674 51.1364 14.2728C51.019 15.287 51.5533 15.5473 52.4373 15.3813C53.347 15.0802 53.9468 14.2703 53.8845 13.4985C53.8331 12.8681 52.9262 12.4458 52.1286 12.3844C51.9378 12.3704 51.7676 12.1695 51.5544 12.3368C50.6623 12.0812 49.8415 11.4948 48.8443 11.7365C48.1063 11.4746 47.3905 10.9258 46.5834 11.5634L46.2148 11.4315C44.8117 10.9136 43.3109 10.7219 41.8243 10.8708L40.6971 10.8328L39.114 10.7044L39.1268 10.7693C39.1268 10.7693 39.1268 10.7693 39.1195 10.7845L39.1159 10.7053L37.0597 10.6329L33.7028 10.3667L31.6341 10.3538L30.1538 10.4988L29.4935 10.4369C29.3545 10.3416 29.1909 10.2894 29.0228 10.2868C28.8548 10.2843 28.6897 10.3314 28.5479 10.4225C28.2027 10.4686 27.8348 10.4027 27.5222 10.622L27.0555 10.6647C26.3684 10.2683 25.7531 10.754 25.1045 10.8269C23.6698 10.8582 22.2244 10.767 20.8126 11.1215C20.5629 11.095 20.31 11.0316 20.0723 11.1728C19.769 11.0675 19.4391 11.071 19.1381 11.1827L17.4688 11.3004L15.5794 11.3639C15.1621 11.1354 14.7672 11.4339 14.3644 11.4212C13.6587 11.231 12.9111 11.2746 12.2318 11.5457C11.5632 11.5783 10.9555 11.099 10.2588 11.3007L6.59117 10.7153C5.30325 10.4644 4.12349 9.81813 3.21306 8.86491C2.66335 8.29448 2.13011 7.95967 1.34854 8.09153C0.387882 8.43091 0.241869 9.03231 1.00891 9.83622C2.25311 11.1357 3.91696 11.9427 5.69945 12.1112C6.49924 12.2932 7.29735 12.4932 8.13131 12.3683L8.78112 12.4133C9.82696 12.7162 10.8784 12.9305 11.9667 12.6445L11.9573 12.6398L13.7347 12.7605C13.9402 12.9366 14.182 12.835 14.4046 12.8553C14.7058 13.069 15.018 12.9902 15.3318 12.8935C16.0953 12.6402 16.9071 12.8117 17.6514 12.6896C20.1035 12.2882 22.5751 12.2318 25.0369 12.0415C25.2402 12.1836 25.4532 12.0936 25.6631 12.0678C26.3435 11.7267 27.2691 12.4186 27.8243 11.5469L28.2777 11.4789C28.5304 11.6728 28.8314 11.7921 29.1473 11.8235C29.4632 11.855 29.7815 11.7973 30.0669 11.657C30.7837 11.6833 31.5128 11.8 32.2125 11.703C32.6426 11.6447 33.2304 11.8421 33.5235 11.2778C34.0048 11.3736 34.3693 11.8413 34.9297 11.7345C35.9164 12.0975 36.9323 11.6773 37.9202 11.797C37.9787 11.8143 38.039 11.825 38.0999 11.8291C38.4895 12.2739 38.9988 11.8393 39.4266 12.0121C40.051 12.2158 40.6714 12.1243 41.295 11.9922C42.8906 12.4176 44.5898 12.0355 46.1889 12.4438C46.3054 12.5011 46.431 12.5441 46.553 12.5946C46.5941 12.5914 46.6333 12.5919 46.6726 12.5924C46.7759 12.6432 46.8874 12.6769 46.9935 12.7267C47.2069 12.7659 47.4025 12.9723 47.6484 12.8141C48.505 12.9794 49.3792 13.1229 50.23 13.3582ZM2.30952 9.03768L2.32456 9.04507L2.30952 9.03768ZM41.8762 11.0136L41.8698 11.0269L41.8781 11.0098L41.8762 11.0136Z" fill="#F43326"></path>
                        <path d="M61.0511 11.9265C60.5026 11.4739 60.0134 10.884 59.1912 10.9654C57.3609 10.0775 55.5244 9.20763 53.707 8.29322C52.8411 7.86763 52.0592 7.24874 51.1771 6.88086C49.0688 6.02991 46.8744 5.42038 44.8022 4.45115C44.2107 4.17449 43.5607 3.84093 42.8461 3.85324C42.4549 3.70084 42.1122 3.35646 41.6277 3.48893C39.444 2.47656 37.033 2.07022 34.8901 0.944233C34.7818 0.8906 34.6604 0.869693 34.5405 0.884017C34.4207 0.898341 34.3075 0.947289 34.2145 1.02498C33.4599 1.1443 33.5185 1.65863 33.6625 2.20319C33.9659 3.34684 35.9837 4.56848 37.4545 4.50568L37.4275 4.41732L37.4508 4.50385C37.5053 4.53062 37.5717 4.53745 37.6326 4.55095C38.1972 4.7581 38.6094 4.42362 39.041 4.15023C41.567 5.01183 44.1203 5.80712 46.6127 6.75773C48.2814 7.39266 49.9773 8.02918 51.5407 8.86329C53.0345 9.65852 54.568 10.4005 56.0243 11.2735C56.5179 11.57 57.4115 11.4323 57.485 12.0313C57.5503 12.556 56.718 12.8412 56.3924 13.3332L56.4527 13.3816L56.3943 13.3341C55.6906 13.6755 55.2336 14.2953 54.7116 14.8526C54.3986 14.8559 54.3953 15.1662 54.2644 15.3364C52.7881 17.1127 51.3117 18.8889 49.8354 20.6652L49.876 20.7063L49.8409 20.6633C48.3878 21.9803 47.21 23.5358 46.0388 25.0969C45.2799 25.7888 44.6888 26.6241 44.076 27.4417C44.0174 27.4736 43.971 27.5242 43.9439 27.5855C43.2177 27.9158 42.8343 28.5999 42.3328 29.1626C42.0274 29.3761 41.7407 29.6159 41.4761 29.8791C41.0865 30.3303 41.2142 30.8411 41.4482 31.329C41.644 31.7372 41.9513 31.7311 42.2827 31.5258C43.1795 31.2465 43.0425 30.2058 43.6433 29.6684C45.9291 27.6606 47.5527 25.0412 49.6876 22.8889C49.7949 22.815 49.8956 22.7319 49.9886 22.6404C51.6533 20.644 53.3172 18.6394 54.9805 16.6266C55.0829 16.5925 55.1862 16.5565 55.2877 16.5196L55.8504 16.822C55.8478 16.8253 55.8459 16.8292 55.845 16.8334L55.9574 16.7737C55.9424 16.7928 55.9236 16.8087 55.9024 16.8204C55.8812 16.8322 55.8579 16.8395 55.8339 16.842C55.8718 16.985 55.8213 17.2534 56.0078 17.2184C56.2866 17.1654 56.2426 16.799 56.3957 16.6021C56.631 16.6826 56.6728 16.4803 56.7536 16.3418C56.9175 16.3286 57.0734 16.2651 57.2006 16.16C57.3277 16.0548 57.42 15.9129 57.465 15.7532C57.7144 15.5639 58.0871 15.5618 58.2425 15.23C58.756 15.1476 59.22 14.8733 59.5425 14.4617C60.2868 14.0746 60.4589 13.1975 61.0467 12.6678C61.0989 12.6217 61.1408 12.5648 61.1696 12.5011C61.1984 12.4374 61.2135 12.3682 61.214 12.2982C61.2144 12.2281 61.2001 12.1587 61.172 12.0947C61.1439 12.0306 61.1027 11.9733 61.0511 11.9265ZM58.6662 11.7839C58.6691 11.7638 58.6733 11.744 58.6787 11.7244C58.6894 11.7648 58.6992 11.8072 58.7071 11.8486C58.6896 11.8297 58.6758 11.8078 58.6662 11.7839ZM54.3397 15.3922C54.3342 15.4036 54.3278 15.4169 54.3223 15.4283L54.2897 15.3465L54.3397 15.3922ZM46.1042 25.183L46.0996 25.1924L46.068 25.1183L46.1042 25.183Z" fill="#F43326"></path>
                    </svg>
                    <span className="factLabel">ㅤFocus</span>
                    <p>Expériences immersives<br/>Storytelling visuel<br/>Textures<br/>Lumières<br/>Détails</p>
                </div>
            </div>
        </section>

        {/* <svg className="arrow-annotation arrow_height" width="57" height="102" viewBox="0 0 37 71" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M35.1529 49.3695C34.9983 48.8878 34.5082 48.7081 34.2368 48.3324C33.8669 48.0132 33.5497 47.6318 33.0985 47.4106C33.0354 47.313 32.951 47.2312 32.8518 47.1713C32.7527 47.1114 32.6412 47.075 32.526 47.0649C32.0679 46.93 31.7484 46.5463 31.311 46.4011C30.5817 46.1605 29.9033 45.7854 29.3105 45.2949C28.6486 44.7464 27.9707 44.2094 27.4337 43.525C27.2917 43.3452 27.1062 43.3671 26.9149 43.4039L26.6859 43.5192C26.2645 43.6609 26.2393 43.9801 26.4844 44.2428C27.1623 44.9815 27.8848 45.6809 28.5902 46.3942C28.7276 46.5336 28.9086 46.4852 29.0735 46.5025C29.1071 46.5687 29.1573 46.6248 29.2192 46.6652C29.2811 46.7056 29.3525 46.729 29.4262 46.733C29.9117 47.293 30.5644 47.5143 31.2583 47.6549L31.7072 48.0144C32.1928 48.3923 32.7722 48.6113 33.2462 49.0296C33.7328 49.449 34.1064 49.9851 34.3323 50.5884C34.5583 51.1916 34.6294 51.8425 34.5391 52.4808C34.4612 53.0638 34.0707 53.5178 33.9963 54.0871L33.6528 54.548H33.0928C32.5535 54.404 32.0175 54.1539 31.4748 54.1378C30.0239 54.0928 28.8284 53.3634 27.5814 52.7838C26.5233 52.2929 25.5053 51.7133 24.3945 51.34C23.7659 50.9274 23.0135 50.7742 22.3757 50.3951C21.4986 49.8731 20.5115 49.6472 19.5908 49.2427C19.3446 49.1632 19.0801 49.1183 18.8556 48.9973C17.7918 48.4211 16.6456 48.0478 15.5176 47.6595C14.3634 47.2619 13.2961 46.6443 12.1064 46.3504C11.981 46.3104 11.8644 46.2467 11.7628 46.1626C11.0391 45.6164 10.1677 45.3456 9.41076 44.8628C8.7317 44.0251 7.86371 43.412 6.99457 42.799C5.83114 41.1201 4.36769 39.6474 3.55924 37.7288L3.44473 37.6136C3.42775 37.3648 3.34901 37.1243 3.21571 36.9141C3.10691 36.3868 2.91352 35.8809 2.64315 35.4161C2.41413 34.9436 2.5504 34.3571 2.18511 33.9261C2.15648 33.8109 2.20915 33.66 2.0706 33.5805C1.99044 33.305 2.19198 33.0504 2.08434 32.7496C1.89425 32.2161 1.84844 31.648 1.81295 31.0765C1.75798 30.1973 1.89769 29.3111 1.78776 28.4457C1.59423 26.9293 1.64691 25.4209 1.7305 23.9068L1.69157 23.886H1.70646L1.7305 23.9102C2.0351 23.3202 1.92974 22.6842 1.93891 22.0665C2.20686 21.6448 2.13931 21.1596 2.19084 20.6976C2.32367 20.3588 2.44962 20.0188 2.3958 19.6432C2.40318 19.6261 2.40699 19.6077 2.40699 19.589C2.40699 19.5704 2.40318 19.552 2.3958 19.5349C2.58017 19.2952 2.59734 19.0094 2.62483 18.7283C3.00615 17.7545 3.13097 16.699 3.57413 15.7449L3.52375 15.7207V15.7034L3.57413 15.7449C3.75506 15.655 3.76078 15.4902 3.78597 15.3128C3.82147 15.0524 3.85125 14.7689 3.97606 14.5488C4.51197 13.6039 4.75589 12.5357 5.25401 11.5712C5.54143 11.0146 5.80252 10.4039 5.85519 9.7471C5.88611 9.70216 5.9216 9.65836 5.94909 9.61112C6.26285 9.06031 6.19873 8.35164 6.53196 7.85614C6.95679 7.22582 7.24536 6.52522 7.67019 5.90873C8.02549 5.41366 8.23287 4.82677 8.26794 4.21712L8.72598 3.68245C9.04661 3.38055 9.37297 3.08325 9.389 2.59467C9.44741 2.03695 9.88484 1.86755 10.3051 1.68318C10.6051 1.40086 10.6715 0.979118 10.9028 0.65647C11.0173 0.46749 11.2395 0.256617 11.0506 0.0595706C10.8799 -0.116734 10.6921 0.1483 10.5124 0.211677C10.431 0.240485 10.3692 0.326909 10.2982 0.384525C9.76918 0.744047 9.37526 1.2453 8.94241 1.70507C8.26336 2.32041 7.6679 2.99913 7.31063 3.86106L6.63959 5.00531L6.51019 5.20466L6.07849 5.82691C5.66854 6.10232 5.53457 6.5183 5.5151 6.97923C5.50549 7.00873 5.49745 7.03873 5.49104 7.06911C5.033 7.28805 5.1979 7.80083 4.99407 8.14422C4.65373 8.69178 4.40788 9.29332 4.26693 9.9234C4.13524 10.5848 3.82605 11.1644 3.55924 11.7671C2.89851 13.2478 2.21259 14.7182 1.84157 16.3072C1.8347 16.3072 1.82899 16.3141 1.82212 16.3234C1.60111 16.5054 1.64003 16.7912 1.56674 17.0286C1.13046 18.4482 0.746856 19.8829 0.576234 21.3659C0.539591 21.4281 0.475458 21.488 0.469733 21.5526C0.394156 22.4514 0.134222 23.3168 0.104449 24.2317C0.071241 25.2388 -0.0627387 26.2402 0.117044 27.245C-0.055868 27.4259 0.00253093 27.6552 0.0437549 27.8454C0.212086 28.7442 0.136501 29.6568 0.236126 30.5625C0.447971 32.4869 0.704477 34.3871 1.3675 36.2216C1.55071 36.7286 1.85073 37.1538 2.05456 37.6389C2.46795 38.6276 2.90539 39.5979 3.50657 40.499C3.96461 41.18 4.73298 41.5868 5.04903 42.3761C5.29867 43.0018 5.81855 43.397 6.29835 43.8222C6.62356 44.1103 7.00603 44.3396 7.29918 44.6565C8.40879 45.8584 9.80926 46.5982 11.2452 47.3068C11.9495 47.6525 12.6846 47.9291 13.3889 48.2759C13.9832 48.5675 14.6096 48.9743 15.347 48.9097C15.6906 49.0399 16.0043 49.2301 16.3776 49.2554C17.2937 49.5677 18.1777 49.9848 19.0755 50.3444C20.0385 50.7304 20.9764 51.1763 21.9829 51.446C22.4879 51.9541 23.2277 51.9622 23.8014 52.2952C25.1217 53.0569 26.5645 53.5478 27.9387 54.2092C28.2948 54.3325 28.5536 54.6702 28.9693 54.6702C29.6266 54.9628 30.2976 55.221 31.0121 55.3224C31.3446 55.37 31.67 55.4587 31.9809 55.5862C32.3817 55.7453 32.439 55.9838 32.2099 56.3272C32.153 56.4222 32.1014 56.5203 32.0553 56.621C31.5675 56.9898 31.1908 57.4841 30.8575 57.9681C30.4087 58.6191 29.7353 59.0974 29.4353 59.8556C28.4562 60.8754 27.4543 61.8767 26.687 63.0821C26.458 63.3909 26.1511 63.6398 26 64.0039C25.5282 64.3876 25.2247 64.9131 24.8549 65.3867L23.4807 67.2304C23.2007 67.5389 22.969 67.8887 22.7937 68.2675C22.6104 68.4357 22.3734 68.5602 22.3448 68.8436L21.8867 69.2954C21.4802 69.4329 21.1222 69.6863 20.8561 70.0248C20.393 70.0621 19.9269 70.0121 19.482 69.8773C19.2198 69.7038 18.9876 69.4881 18.7949 69.2389C18.6152 68.9865 18.3987 68.854 18.1079 69.0637C17.9098 69.1375 17.9567 69.2735 18.0002 69.4279C18.1434 69.9429 18.5121 70.2633 18.9255 70.5514C19.6836 71.0284 20.4806 71.1391 21.3302 70.8083C21.7883 70.5514 22.3746 70.6793 22.8189 70.3474C23.1464 70.1711 23.2036 69.8035 23.3914 69.5293C23.625 69.2658 23.8594 69.0023 24.0945 68.7388C24.3361 68.6938 24.3911 68.4622 24.4907 68.2974C24.7003 67.9518 24.8343 67.5519 25.1125 67.2408C25.8282 66.7556 26.2233 66.0216 26.6012 65.2818L27.2814 64.4752C27.6604 63.8679 28.2696 63.4301 28.541 62.7467C29.0941 62.5566 29.259 62.0127 29.5716 61.6036C30.2394 61.1522 30.7578 60.5101 31.0602 59.7599C31.3774 59.2057 32.0359 58.966 32.3199 58.3772C32.4848 58.0407 32.5683 57.6385 32.9451 57.4876C32.9693 57.4826 32.9928 57.4745 33.0149 57.4634C33.0644 57.4493 33.115 57.4393 33.1661 57.4334C33.2806 57.4207 33.3527 57.256 33.3527 57.1096C33.7925 56.7121 34.1933 56.2822 34.8082 56.1105C34.8818 56.0869 34.9493 56.0473 35.006 55.9945C35.0627 55.9417 35.1071 55.877 35.1362 55.805C35.1653 55.7329 35.1782 55.6554 35.1742 55.5778C35.1701 55.5001 35.1492 55.4243 35.1128 55.3558C34.9433 55.0689 35.1025 54.9064 35.1769 54.6944C35.4895 54.5146 35.4529 54.1355 35.6349 53.8877C36.3461 52.2699 35.9544 50.7961 35.1529 49.3695ZM19.4648 70.5721H19.4774H19.4648ZM2.3271 18.8654L2.29275 18.8504V18.8377C2.30509 18.8458 2.31659 18.855 2.3271 18.8654ZM29.2143 46.476H29.1937L29.1868 46.4691L29.2143 46.476ZM3.51916 13.6154C3.53175 13.6266 3.54326 13.6389 3.55352 13.6523L3.50542 13.665L3.51916 13.6154ZM1.72249 20.4521C1.72249 20.4521 1.72249 20.4429 1.72249 20.4394H1.73966L1.72249 20.4521ZM10.3039 46.3735V46.3631V46.3735ZM13.406 47.202C13.406 47.202 13.3992 47.1905 13.3969 47.1847H13.4038C13.4054 47.1896 13.4078 47.1942 13.4106 47.1985L13.406 47.202ZM13.1667 47.1801C13.1815 47.1915 13.1934 47.2062 13.2018 47.223C13.2101 47.2397 13.2146 47.2582 13.2148 47.2769C13.1931 47.2458 13.1736 47.2135 13.1541 47.1801H13.1667ZM15.9035 48.6528C15.9035 48.6608 15.9104 48.6666 15.9138 48.6758H15.8932C15.897 48.6673 15.9016 48.6592 15.907 48.6516L15.9035 48.6528ZM3.20426 37.3923V37.3854V37.3923ZM31.5916 47.6295H31.5984H31.5916ZM0.592254 21.9328C0.589135 21.9416 0.585307 21.9501 0.580805 21.9582V21.9305L0.592254 21.9328ZM22.5498 68.8321L22.5429 68.8413L22.5314 68.8321H22.5498ZM23.341 69.2931L23.3502 69.2873L23.341 69.2931ZM23.8151 68.7169L23.822 68.7088C23.822 68.7088 23.822 68.7146 23.822 68.7169H23.8151Z" fill="#F43326"></path>
        </svg> */}

        <section className="about" data-scroll-section>
          <div className="aboutGrid">
            <div className="aboutLeft">
              {/* <p className="kicker">À propos</p> */}
              <p className="bigLine">
                Des interfaces
                <br />
                qui{' '}
                <span className="stretchWord" aria-label="bougent" ref={stretchRef}>
                  <span className="ch ch--edge">b</span>
                  <span className="ch ch--near">o</span>
                  <span className="ch ch--mid">u</span>
                  <span className="ch ch--mid">g</span>
                  <span className="ch ch--mid">e</span>
                  <span className="ch ch--near">n</span>
                  <span className="ch ch--edge">t</span>
                </span>
              </p>
              <br/>
              <p className="aboutSmall">
                Je cherche le bon équilibre entre design, sensation et performance. Surtout quand il
                y a de l’animation, de la 3D, ou des détails qui doivent rester fluides sur mobile. Ne pas 
                faire de la 3D uniquement pour en faire mais pour provoquer une sensation ou une réelle valeur ajoutée... 
              </p>

                <svg class="arrow-annotation large_arrow_right" width="66" height="180" viewBox="0 0 86 244" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21.3087 31.6223C22.1318 34.3498 23.9706 36.4409 25.8818 38.3898C27.2119 39.7332 28.6183 40.9988 30.0938 42.1802C30.3351 42.6714 30.8665 42.8451 31.2245 43.221C32.3283 44.3881 33.1057 45.8127 34.1875 46.9972C34.5313 47.3731 34.9255 47.629 35.4711 47.5216C35.8796 47.5784 36.2344 47.4252 36.3022 47.0304C36.3779 46.5913 35.8528 46.7872 35.6604 46.5882C35.5815 46.5045 35.4317 46.4887 35.3134 46.4413C34.8404 45.6406 34.4036 44.8177 33.8942 44.0407C33.183 42.9557 32.0729 42.2339 31.2623 41.231C31.0952 41.0241 30.9201 40.7572 30.589 40.7398L30.5495 40.7999L30.5905 40.7414C30.0307 39.9012 29.277 39.2347 28.5673 38.5303C28.1668 38.1371 27.864 37.608 27.2758 37.4248C26.429 36.3035 25.3441 35.3795 24.6203 34.154C23.8964 32.9284 22.7863 31.845 22.9566 30.1945C23.0197 29.5865 22.7879 28.9548 22.7674 28.3309C22.7169 26.7816 22.3605 25.2212 23.0417 23.6971C23.2893 23.1412 23.3414 22.4921 23.4596 21.8809C23.6977 20.6332 24.0447 19.4013 24.3806 18.1883C24.6959 17.0559 25.035 15.8983 25.4686 14.7801C25.7462 14.0631 26.0821 13.3587 26.2871 12.6069C26.6592 11.2566 27.4225 10.0563 27.741 8.69014C28.121 8.43587 28.3402 8.06946 28.5421 7.66199C28.7439 7.25452 28.8859 6.8581 29.1729 6.50117C29.8036 5.71149 30.3918 4.89655 30.958 4.06424C31.2276 3.66782 31.2355 3.11663 31.6849 2.80076C32.4828 1.88158 33.3186 1.01294 34.5234 0.627575C34.8072 0.616519 34.9823 0.487013 35.0548 0.0921761C34.197 0.417522 33.4148 -0.291605 32.6137 0.149033C32.456 0.254849 32.4403 0.54229 32.169 0.509124C32.065 0.480695 32.0113 0.537553 31.9877 0.624417C31.5462 0.88185 31.3569 1.35408 31.0415 1.72996C29.4141 3.45935 27.8593 5.23453 27.1276 7.57355C27.068 7.77368 26.9712 7.96071 26.8422 8.12474C26.0537 9.10393 25.7493 10.35 25.0839 11.4035C24.3963 12.4901 24.5477 13.8704 23.9501 15.0044C23.477 15.2681 23.3524 15.7388 23.1852 16.211C22.7248 17.5108 22.7295 18.8785 22.5308 20.2162C22.2154 20.6553 21.8054 21.0864 21.7944 21.6234C21.755 23.457 21.0201 25.158 20.9034 26.9932C20.8056 28.5583 21.2598 30.0177 21.2961 31.537C21.2955 31.5659 21.2998 31.5947 21.3087 31.6223ZM21.9048 24.6526L21.93 24.6842L21.8906 24.7521C21.8906 24.7189 21.9 24.6858 21.9048 24.6526ZM34.0472 0.378039C34.0472 0.363825 34.0472 0.352769 34.0472 0.336975C34.0563 0.343286 34.0665 0.348085 34.0771 0.351189C34.0614 0.360665 34.0519 0.368563 34.0377 0.378039H34.0472ZM32.2258 0.551767C32.2048 0.546914 32.1846 0.538928 32.1659 0.528076L32.2258 0.551767Z" fill="#F43326"></path>
                    <path d="M84.9727 202.95C85.1825 202.174 84.364 201.29 83.6166 201.48C83.0142 201.637 82.4244 201.824 81.7905 201.865C81.1565 201.906 80.7576 201.672 80.469 201.118C79.4708 199.193 78.4852 197.26 77.435 195.363C76.4336 193.558 75.3755 191.782 74.3111 190.013C72.4881 186.983 69.9761 184.516 67.6249 181.921C67.1975 181.447 66.8522 181.018 66.9562 180.354C67.0614 180.133 67.1712 179.912 67.2858 179.691C67.5255 179.497 67.6942 179.285 67.4924 178.939C66.9925 179.176 66.7922 179.58 66.6487 180.027C66.3239 180.079 66.1268 180.343 65.8603 180.501C65.5449 180.501 65.3982 180.747 65.2295 180.961C64.9661 181.126 64.7564 181.308 65.0639 181.618C65.4692 182.965 66.4548 183.871 67.4482 184.776C67.5634 185.419 68.2998 185.517 68.5521 186.04C68.5709 186.201 68.6434 186.351 68.7579 186.466C68.8723 186.58 69.0221 186.653 69.1829 186.672C69.3985 186.978 69.6652 187.245 69.9713 187.461C70.4286 188.477 71.2818 189.214 71.8511 190.146C72.4266 191.138 73.1914 192.007 73.6503 193.079C73.8569 193.562 74.1013 194.06 74.535 194.412C74.5192 195.083 75.0853 195.456 75.3582 195.979C76.1466 197.501 76.9903 198.995 77.8308 200.492C78.3811 201.476 78.3291 201.598 77.2095 201.69C77.1653 201.69 77.1259 201.743 77.0833 201.773C76.104 201.915 75.1058 201.914 74.1329 202.042C72.0545 202.317 70.0218 201.87 67.9671 201.824C67.6264 201.824 67.3773 201.489 67.0114 201.524C65.7026 201.647 64.43 201.431 63.1732 201.094C62.9534 201.015 62.7297 200.948 62.503 200.892C60.3851 200.492 58.3004 199.929 56.2173 199.4C53.2669 198.649 50.3574 197.714 47.4338 196.849C44.857 196.086 42.1826 195.664 39.5649 195.034C37.9674 194.649 36.3321 194.56 34.7205 194.298C32.8755 193.995 31.0273 193.627 29.1681 193.509C27.4966 193.407 25.8818 192.972 24.215 192.893C22.9171 192.829 21.6651 192.458 20.394 192.21C18.4695 191.813 16.5925 191.213 14.7943 190.419C13.5123 189.871 12.4289 188.988 11.1674 188.391C10.9482 187.668 10.3111 187.359 9.7592 186.97C9.59046 186.455 9.15207 186.133 8.84299 185.722C7.89682 184.459 7.00428 183.15 6.32935 181.723C5.62261 180.209 5.00395 178.655 4.47645 177.069C3.90629 175.411 3.47638 173.709 3.19125 171.979C2.96417 170.567 2.52105 169.199 2.43589 167.735C2.30659 165.508 1.94074 163.286 1.86662 161.053C1.77201 158.191 1.92655 155.322 1.81301 152.461C1.69947 149.601 2.37282 146.75 2.17255 143.878C2.22306 143.821 2.26312 143.757 2.29082 143.686C2.61567 141.997 2.53367 140.269 2.69925 138.561C2.95629 135.921 3.43883 133.294 3.44198 130.628C3.95606 129.941 3.62806 129.136 3.75737 128.395C3.79783 128.332 3.81717 128.258 3.81257 128.184C3.97026 127.552 3.98761 126.906 4.04911 126.262C4.17684 124.94 4.34714 123.622 4.47803 122.301C4.63572 120.748 5.10881 119.229 4.98581 117.646C5.25389 115.863 5.69227 114.099 5.61658 112.276C5.64099 112.236 5.65508 112.191 5.65758 112.144C6.16851 111.555 5.79478 110.827 5.96193 110.189C6.43501 108.39 6.40662 106.534 6.61162 104.707C6.84562 104.427 6.98774 104.083 7.01848 103.72C7.24661 101.363 7.47473 99.0073 7.70286 96.6519C8.23271 94.7899 8.25007 92.8457 8.61749 90.9663C8.97073 90.5304 8.70106 90.0013 8.84299 89.518C9.2656 88.0745 9.36968 86.5646 9.67246 85.0958C9.94842 83.7534 10.1723 82.3873 10.3837 81.0259C10.8394 80.6231 10.6092 80.0419 10.7511 79.5539C11.1406 78.2162 11.4576 76.8564 11.8045 75.506C12.6466 72.3868 13.4918 69.2692 14.4979 66.1989L14.4253 66.1547L14.4916 66.2037C14.7928 65.1976 15.0987 64.1932 15.3936 63.1855C15.8351 61.6746 16.2125 60.1479 16.5258 58.6054C17.1566 58.2896 17.0431 57.5757 17.3253 57.0703C17.9861 55.889 18.3235 54.5781 18.5869 53.2515C19.0213 52.6505 19.3283 51.9668 19.4889 51.2425C19.9225 49.0314 20.7757 46.9514 21.3418 44.7861C21.8622 42.7914 22.9029 40.8709 22.2044 38.6788C22.0467 38.1955 21.9379 38.1276 21.5578 38.3835L21.2787 38.4198C20.8277 38.6836 20.3168 38.9915 20.342 39.5475C20.4524 41.9828 19.6261 44.325 19.6734 46.7493L19.727 46.7824L19.6939 46.8014L19.6876 46.7524C19.3154 47.4568 18.8692 48.1896 18.825 48.9635C18.7509 50.2539 18.3519 51.432 17.9577 52.626C17.6727 52.9296 17.5166 53.3323 17.5224 53.7489C17.4814 55.4057 16.4848 56.6929 15.9045 58.1395L15.7468 58.288C15.403 58.457 15.3731 58.8313 15.2154 59.1187C14.7423 59.9874 14.7155 61.0061 14.3323 61.9063C14.0559 62.5728 13.8411 63.2632 13.6905 63.9689C13.4492 64.0921 13.4476 64.3227 13.3751 64.5533C13.0392 65.7141 13.0865 66.9602 12.574 68.0784C12.287 68.4543 12.1829 68.8965 12.1009 69.3513C11.5474 71.0728 10.8599 72.7643 10.4688 74.5237C9.62989 78.2952 8.39514 81.9782 7.98356 85.8476C7.79275 87.6291 7.4679 89.3964 7.20455 91.17C6.95381 91.5522 7.15724 91.9944 7.04685 92.3956C6.66366 92.9484 7.2692 93.5011 7.06578 94.0334C6.72043 94.9431 7.03898 96.017 6.35616 96.8462C6.27101 96.9504 6.28993 97.1415 6.25996 97.2916C6.05496 97.4069 6.14485 97.6074 6.11489 97.7748C5.82316 98.1634 6.0455 98.6182 5.9572 99.0383C5.68439 99.2594 5.87362 99.5706 5.7995 99.828C5.49673 100.281 5.70173 100.776 5.67965 101.249C5.66343 101.301 5.65131 101.354 5.64339 101.407C5.32565 102.806 5.16695 104.237 5.17031 105.672C4.65871 108.064 4.34228 110.493 4.22414 112.937C4.05544 113.365 3.95693 113.819 3.93242 114.279C3.74791 116.19 3.34106 118.069 3.14395 119.982C3.02095 121.173 2.69452 122.351 2.68033 123.556C2.65825 125.387 2.34128 127.189 2.1142 128.991C1.74205 131.928 1.44558 134.874 1.116 137.814C0.832152 140.352 0.570384 142.902 0.58773 145.465C0.307036 146.664 0.412691 147.897 0.341729 149.108C0.262882 150.411 0.221879 151.73 0.0925703 153.024C0.00899267 153.848 0.160377 154.628 0.130415 155.429C0.0894149 156.512 -0.0272749 157.597 0.00584072 158.678C0.0626104 160.69 0.163534 162.7 0.321228 164.706C0.414267 165.873 0.478917 167.056 0.661842 168.204C1.00246 170.272 1.29262 172.344 1.7657 174.398C2.12113 176.017 2.56171 177.616 3.08559 179.189C3.86144 181.452 4.84072 183.646 6.32147 185.612C7.64609 187.373 8.96126 189.099 10.7369 190.429C11.5711 191.061 12.4431 191.648 13.4051 192.019C14.6319 192.493 15.9282 192.801 17.1897 193.178C19.1798 193.771 21.1588 194.36 23.2672 194.456C24.5288 194.515 25.8376 194.537 27.0724 194.793C28.807 195.151 30.5574 195.123 32.2857 195.385V195.353L32.2715 195.379C32.3788 195.536 32.5475 195.495 32.6989 195.517C35.2646 195.892 37.8286 196.252 40.377 196.756C42.7786 197.23 45.0889 198.005 47.448 198.611C49.2788 199.085 51.0749 199.679 52.912 200.145C54.1878 200.461 55.514 200.676 56.7298 201.164C58.8713 202.024 61.1452 202.247 63.3529 202.794C64.7548 203.141 66.2734 202.926 67.587 203.692C67.6832 203.747 67.9024 203.751 67.9576 203.692C68.2966 203.277 68.6893 203.488 69.0977 203.509C69.9839 203.556 70.8655 203.88 71.7407 203.716C73.0479 203.47 74.3536 203.632 75.6593 203.577C76.0248 203.577 76.388 203.519 76.7348 203.403C77.4176 203.403 78.1004 203.428 78.7848 203.421C78.968 203.416 79.1499 203.451 79.3178 203.525C79.4857 203.598 79.6354 203.708 79.7562 203.845C80.3744 204.512 80.972 205.196 81.6138 205.835C81.7428 205.945 81.8407 206.086 81.8977 206.246C81.9547 206.405 81.9689 206.577 81.9387 206.744C81.8031 207.108 81.3647 207.269 81.3079 207.691C80.2642 208.371 79.3988 209.291 78.7848 210.376C77.2079 211.701 76.1135 213.419 74.8803 215.03C72.8303 217.696 71.2282 220.635 69.4872 223.492C69.0141 223.99 68.7618 224.598 68.5411 225.23C67.6123 226.748 66.5699 228.208 65.7925 229.799C65.097 231.22 64.0342 232.421 63.4964 233.918C63.4854 233.918 63.4744 233.934 63.4649 233.96L61.1626 237.076L60.9686 237.37C60.5685 237.728 60.2431 238.162 60.0114 238.646L58.0071 240.548C57.3385 240.706 57.0783 241.461 56.4302 241.653C55.5471 241.811 54.6656 241.969 53.7841 242.127C53.5349 242.173 53.4577 242.351 53.4687 242.59C53.4797 242.795 53.5665 242.971 53.7699 242.985C54.8864 243.062 55.9776 243.021 56.908 242.261C58.22 241.762 59.1961 240.801 60.1928 239.869C60.6564 239.438 61.243 239.12 61.4843 238.482C61.8611 238.277 62.1437 237.933 62.2727 237.523C62.7283 237.125 63.1031 236.642 63.3766 236.102C63.5432 235.979 63.6937 235.836 63.8244 235.676C65.2673 233.354 66.8979 231.148 68.0995 228.677C69.9399 225.936 71.6246 223.093 73.1457 220.162C73.1599 220.162 73.1741 220.143 73.1851 220.111C74.207 218.818 75.355 217.611 75.9842 216.044C76.3882 215.636 76.7095 215.153 76.9304 214.623C77.271 214.427 77.6621 214.234 77.8213 213.887C78.2834 212.882 78.9993 212.086 79.7625 211.319C80.0779 211.303 80.1662 211.055 80.3034 210.845C80.644 210.313 80.9957 209.787 81.3426 209.266C82.2036 208.225 83.44 207.686 84.487 206.897C84.6447 206.857 84.6305 206.717 84.6637 206.603C85.009 206.216 85.1052 205.775 84.6069 205.508C83.9919 205.178 83.4873 204.698 82.8817 204.368C82.9558 204.153 82.8218 203.877 83.0694 203.71C83.6733 203.383 84.4886 203.583 84.9727 202.966V202.95ZM53.9071 242.47C53.9071 242.47 53.9071 242.484 53.9071 242.49H53.8708L53.9071 242.47ZM1.8682 169.442H1.88397V169.471C1.87786 169.462 1.87258 169.452 1.8682 169.442ZM84.1606 206.748H84.1795C84.176 206.757 84.1717 206.765 84.1669 206.774C84.1608 206.763 84.157 206.752 84.1559 206.74L84.1606 206.748ZM18.5632 53.2151L18.5948 53.2451L18.5632 53.2151ZM12.6355 68.0957L12.5898 68.061L12.6355 68.0957ZM62.2649 236.1V236.118H62.2254C62.2392 236.111 62.2534 236.105 62.268 236.1H62.2649Z" fill="#F43326"></path>
                </svg>
            </div>

            <div className="aboutRight">
              <div className="aboutCard">
                <p className="aboutLabel">Parcours</p>
                <p className="aboutText">
                  Né à Abbeville<br/>
                  Grandi à Saint‑Germain<br/>
                  Études à Paris<br />
                  Travail sur Paris
                </p>
              </div>

              <div className="aboutCard">
                <p className="aboutLabel">Origine</p>
                <p className="aboutText">
                  J’ai commencé par créer en cuisine et en salle : rythme, précision, sens du détail.
                  Aujourd’hui, je retrouve exactement la même chose dans le code.
                </p>
              </div>

              <div className="aboutCard">
                <p className="aboutLabel">Intérêts</p>
                <p className="aboutText">
                  Cuisine<br/>Cinéma<br/>Musique<br/>Grandes randonnées à pied ou à vélo                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="principles" data-scroll-section ref={principlesRef}>
          <div className="principlesSticky">
            <div className="principlesDeck">
              <article className="principleCard card1" data-rank="A" data-suit="♥" data-suit-name="hearts">
                <span className="cardCorner tl" aria-hidden="true">
                  <span className="rank">A</span>
                  <span className="suit">♥</span>
                </span>
                <span className="cardCorner br" aria-hidden="true">
                  <span className="rank">A</span>
                  <span className="suit">♥</span>
                </span>
                <div className="cardCenter">
                  {/* <p className="kicker">Méthode</p> */}
                  <h2 className="principlesTitle">Trois règles simples</h2>
                  <p className="principleText">
                    <br/>Une hiérarchie claire, <br/>peu de texte, <br/>et des choix typographiques <br/>clairs.
                  </p>
                </div>
              </article>

              <article className="principleCard card2" data-rank="K" data-suit="♣" data-suit-name="clubs">
                <span className="cardCorner tl" aria-hidden="true">
                  <span className="rank">K</span>
                  <span className="suit">♣</span>
                </span>
                <span className="cardCorner br" aria-hidden="true">
                  <span className="rank">K</span>
                  <span className="suit">♣</span>
                </span>
                <div className="cardCenter">
                  <p className="principleTitle">Sensation ensuite</p>
                  <p className="principleText">
                    <br/>Micro‑interactions, <br/>easing, <br/>timing. <br/><br/>Les animations doivent raconter quelque chose.
                  </p>
                </div>
              </article>

              <article className="principleCard card3" data-rank="Q" data-suit="♠" data-suit-name="spades">
                <span className="cardCorner tl" aria-hidden="true">
                  <span className="rank">Q</span>
                  <span className="suit">♠</span>
                </span>
                <span className="cardCorner br" aria-hidden="true">
                  <span className="rank">Q</span>
                  <span className="suit">♠</span>
                </span>
                <div className="cardCenter">
                  <p className="principleTitle">Perf toujours</p>
                  <p className="principleText">
                    <br/>Smooth sur desktop, solide sur mobile.
                  </p>
                  <p className="principleText">
                    <br/>Pas d’effet si ça lag.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="confettiSection" ref={confettiSectionRef} data-scroll-section>
            <div className="confettiStage">
                <Confetti key={confettiKey} mode="boom" particleCount={900} />
            </div>
        </section>

        <section className="projectsIntro" data-scroll-section>
            <div className="sectionHeader">
                <h2>Projets</h2>
                <p ref={scatterRef}>
                    <ScatterText text={'Écrans, \nprototypes \net \nexpériences \ninteractives.'} />
                </p>
            </div>
        </section>

        <section className="projects" data-scroll-section id="projects">
          <div className="galleryGrid">
            {[
              {
                src: asset('assets/images/carousel/portfolio_home.webp'),
                title: 'Homepage Portfolio',
                desc: 'Landing page,\nforme générative,\ndark mode,\nShader (GLSL)',
                size: 'lg',
                speed: -1.2,
              },
              {
                src: asset('assets/images/carousel/devxr_immersif.webp'),
                title: 'DevXR Site immersif',
                desc: 'Expérience 3D,\nWebXR,\nSite immersif,\nSalles cachées,\nforme génératives,\nMini jeux,\nQR Code 3D.',
                size: 'xl',
                speed: 1.8,
                pos: 'center',
                // textSpeed removed
              },
              {
                src: asset('assets/images/carousel/catacombes.webp'),
                title: 'Catacombes',
                desc: 'Rendu réaliste,\nambiance,\nPhotogrammetrie,\nPhotorealiste,\nsite immersif,\nCartes rendu AR,\nWebXR,\n3D / AR / VR,\nSEO.',
                size: 'lg',
                speed: -0.8,
                pos: 'left',
                // textSpeed removed
              },
              {
                src: asset('assets/images/carousel/aerobay_home.webp'),
                title: 'Homepage Aerobay',
                desc: 'Structure,\nlisibilité,\nrapidité de chargement,\nSEO',
                size: 'xl',
                speed: 1.3,
                pos: 'right',
                // textSpeed removed
              },
              {
                src: asset('assets/images/carousel/aerobay_intranet.webp'),
                title: 'Aerobay Intranet',
                desc: 'UI interne & composants,\nrapidité de chargement,\nrefonte graphique complete',
                size: 'sm',
                speed: -1.4,
              },
              {
                src: asset('assets/images/carousel/devxr_classic.webp'),
                title: 'DevXR site classique',
                desc: 'Interface,\ninteractions,\n3D,\nVideo sur elements 3D,\nrapidité de chargement',
                size: 'md',
                speed: 0.9,
              },
              {
                src: asset('assets/images/carousel/leon_cyprien.webp'),
                title: 'Léon Cyprien',
                desc: 'Direction artistique,\n3D,\nExport photos,\nFormes geometriques complexes',
                size: 'sm',
                speed: 1.8,
              },
            //   {
            //     src: asset('assets/images/carousel/sophie_marechal_avocat.webp'),
            //     title: 'Site vitrine',
            //     desc: 'Sobriété,\ntypographie,\nSEO',
            //     size: 'md',
            //     speed: -0.7,
            //   },
              {
                src: asset('assets/images/carousel/portfolio_old.webp'),
                title: 'Portfolio (ancien)',
                desc: 'Itérations,\névolution,\nBlender,\nWebXR,\n3D / AR / VR,\nsite immersif',
                size: 'sm',
                speed: 1.3,
              },
              {
                src: asset('assets/images/carousel/cv.webp'),
                title: 'CV',
                desc: 'Version 3D,\nShaders (GLSL),\nScroll animation,\nBlender,\nMixamo,\nLiquidGlass',
                size: 'xs',
                speed: -1.0,
              },
            //   {
            //     src: asset('assets/images/carousel/work_.png'),
            //     title: 'Work',
            //     desc: 'Sélection de projets.',
            //     size: 'md',
            //     speed: 0.6,
            //   },
            ].map((item, idx) => {
              const pos = item.pos || (idx % 3 === 0 ? 'left' : idx % 3 === 1 ? 'center' : 'right');
              const textSpeed = (Number(item.speed) || 0) * 0.78;
              return (
                <div
                  key={item.src}
                  className={`galleryItem pos-${pos}`}
                  data-scroll
                  data-scroll-speed={String(Number(item.speed) || 0)}
                >
                  <div className="galleryCardWrap">
                    <figure className="galleryCard">
                      <img className="galleryImg" src={item.src} alt={item.title} loading="lazy" />
                    </figure>
                    <div
                        className="gallerySide"
                        data-scroll
                        data-scroll-speed={String(textSpeed)}
                        data-pref-corner={(() => {
                            const prefs = ['tr', 'br', 'tl', 'bl'];
                            return prefs[idx % prefs.length];
                        })()}
                    >
                      <h3 className="gallerySideTitle">{item.title}</h3>
                      <p className="gallerySideDesc">{item.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="footerBlock" data-scroll-section>
        </section>
      </div>
    </div>
  );
}
